import { useState, useEffect } from 'react';
import { supabase } from '../lib/supabaseClient';
import AppLayout from '../components/layout/AppLayout';
import type { Strain } from '../types/strain';
import StrainDetailsModal from '../components/modals/StrainDetailsModal';
import EmptyState from '../components/ui/EmptyState';
import { Search, Plus, Edit2, Trash2, Sprout, Zap, Info } from 'lucide-react';

export default function Strains() {
    const [strains, setStrains] = useState<Strain[]>([]);
    const [loading, setLoading] = useState(true);
    const [showForm, setShowForm] = useState(false);
    const [editingId, setEditingId] = useState<string | null>(null);
    const [searchTerm, setSearchTerm] = useState('');
    const [selectedStrain, setSelectedStrain] = useState<Strain | null>(null);
    const [activeFilter, setActiveFilter] = useState<'all' | 'indica' | 'sativa' | 'hybrid'>('all');

    // Form state
    // ... same states ...
    const [name, setName] = useState('');
    const [strainType, setStrainType] = useState<'indica' | 'sativa' | 'hybrid' | 'unknown'>('hybrid');
    const [genetics, setGenetics] = useState('');
    const [defaultThc, setDefaultThc] = useState<number | undefined>(undefined);
    const [notes, setNotes] = useState('');
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        fetchStrains();
    }, []);

    const fetchStrains = async () => {
        const { data, error } = await supabase
            .from('strains')
            .select('*')
            .order('name');

        if (error) {
            console.error('Error fetching strains:', error);
        } else if (data) {
            setStrains(data);
        }
        setLoading(false);
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setSaving(true);

        if (editingId) {
            // Update existing strain
            const { error } = await supabase
                .from('strains')
                .update({
                    name,
                    strain_type: strainType,
                    genetics: genetics || null,
                    default_thc: defaultThc || null,
                    notes: notes || null,
                })
                .eq('id', editingId);

            if (error) {
                console.error('Error updating strain:', error);
                alert('Error updating strain.');
            } else {
                resetForm();
                fetchStrains();
            }
        } else {
            // Insert new strain
            const { error } = await supabase.from('strains').insert({
                name,
                strain_type: strainType,
                genetics: genetics || null,
                default_thc: defaultThc || null,
                notes: notes || null,
            });

            if (error) {
                console.error('Error adding strain:', error);
                alert('Error adding strain. It may already exist.');
            } else {
                resetForm();
                fetchStrains();
            }
        }
        setSaving(false);
    };

    const handleEdit = (strain: Strain) => {
        setEditingId(strain.id);
        setName(strain.name);
        setStrainType(strain.strain_type);
        setGenetics(strain.genetics || '');
        setDefaultThc(strain.default_thc || undefined);
        setNotes(strain.notes || '');
        setShowForm(true);
        window.scrollTo({ top: 0, behavior: 'smooth' });
    };

    const handleDelete = async (id: string) => {
        if (!confirm('Are you sure you want to delete this strain? This cannot be undone.')) {
            return;
        }

        const { error } = await supabase
            .from('strains')
            .delete()
            .eq('id', id);

        if (error) {
            console.error('Error deleting strain:', error);
            alert('Error deleting strain. It may be linked to existing products.');
        } else {
            fetchStrains();
        }
    };

    const resetForm = () => {
        setName('');
        setStrainType('hybrid');
        setGenetics('');
        setDefaultThc(undefined);
        setNotes('');
        setShowForm(false);
        setEditingId(null);
    };

    const typeColors = {
        indica: 'bg-indigo-500/20 text-indigo-400 border border-indigo-500/30',
        sativa: 'bg-emerald-500/20 text-emerald-400 border border-emerald-500/30',
        hybrid: 'bg-amber-500/20 text-amber-400 border border-amber-500/30',
        unknown: 'bg-zinc-500/20 text-zinc-400 border border-zinc-500/30',
    };

    if (loading) {
        return (
            <AppLayout title="Library">
                <div className="flex flex-col items-center justify-center min-h-[60vh]">
                    <Sprout className="text-accent-purple animate-pulse mb-4" size={48} />
                    <p className="text-dark-muted animate-pulse font-medium">Indexing genetic data...</p>
                </div>
            </AppLayout>
        );
    }

    return (
        <AppLayout title="Library">
            <div className="space-y-6">
                <div className="flex items-center justify-between">
                    <div>
                        <h2 className="text-2xl font-black tracking-tight uppercase">Genetic Library</h2>
                        <p className="text-[10px] text-dark-muted font-bold tracking-widest uppercase mt-1">Strains & Ancestry</p>
                    </div>
                    <button
                        onClick={() => {
                            if (showForm) resetForm();
                            else setShowForm(true);
                        }}
                        className={`flex items-center gap-2 px-4 py-2 rounded-xl text-xs font-bold transition-all ${showForm ? 'bg-dark-surface border border-dark-border text-dark-muted' : 'bg-accent-purple text-white shadow-lg shadow-accent-purple/20 active:scale-95'
                            }`}
                    >
                        {showForm ? 'Cancel' : <><Plus size={14} /> New Strain</>}
                    </button>
                </div>

                {/* Filters & Search */}
                {!showForm && (
                    <div className="space-y-4">
                        {/* Filter Pills */}
                        <div className="flex items-center gap-2 overflow-x-auto scrollbar-hide pb-1">
                            {(['all', 'indica', 'sativa', 'hybrid'] as const).map((type) => (
                                <button
                                    key={type}
                                    onClick={() => setActiveFilter(type)}
                                    className={`px-4 py-1.5 rounded-full text-[10px] font-black uppercase tracking-widest transition-all border ${activeFilter === type
                                        ? 'bg-white text-dark-bg border-white scale-105 shadow-lg shadow-white/10'
                                        : 'bg-dark-surface border-dark-border text-dark-muted hover:text-white hover:border-white/30'
                                        }`}
                                >
                                    {type}
                                </button>
                            ))}
                        </div>

                        <div className="relative group">
                            <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                                <Search size={18} className="text-dark-muted group-focus-within:text-accent-purple transition-colors" />
                            </div>
                            <input
                                type="text"
                                placeholder="Find heritage, effects, or names..."
                                className="input pl-11 h-12 bg-dark-surface/50 border-dark-border/50 focus:border-accent-purple/50 focus:ring-accent-purple/5 transition-all font-bold text-sm"
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                            />
                        </div>
                    </div>
                )}

                {/* Add/Edit Form */}
                {showForm && (
                    <form onSubmit={handleSubmit} className="card bg-dark-surface/50 border-accent-purple/20 space-y-6 animate-in slide-in-from-top-4 duration-300">
                        <div className="flex items-center gap-3">
                            <div className="p-2.5 bg-accent-purple/10 rounded-2xl border border-accent-purple/20">
                                <Plus size={20} className="text-accent-purple" />
                            </div>
                            <div>
                                <h2 className="text-lg font-black tracking-tight">{editingId ? 'Modify Genetics' : 'Register New Strain'}</h2>
                                <p className="text-[10px] text-dark-muted font-bold uppercase tracking-widest">Metadata Entry</p>
                            </div>
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div className="space-y-1.5 md:col-span-1">
                                <label htmlFor="name" className="text-[10px] font-black text-dark-muted uppercase tracking-widest">Strain Name *</label>
                                <input
                                    id="name"
                                    type="text"
                                    value={name}
                                    onChange={(e) => setName(e.target.value)}
                                    className="input h-12 text-sm font-bold bg-dark-bg"
                                    placeholder="e.g., Blue Dream"
                                    required
                                />
                            </div>

                            <div className="grid grid-cols-2 gap-4">
                                <div className="space-y-1.5">
                                    <label htmlFor="type" className="text-[10px] font-black text-dark-muted uppercase tracking-widest">Lineage Type *</label>
                                    <select
                                        id="type"
                                        value={strainType}
                                        onChange={(e) => setStrainType(e.target.value as any)}
                                        className="input h-12 text-sm font-bold bg-dark-bg appearance-none cursor-pointer"
                                    >
                                        <option value="indica">Indica</option>
                                        <option value="sativa">Sativa</option>
                                        <option value="hybrid">Hybrid</option>
                                        <option value="unknown">Unknown</option>
                                    </select>
                                </div>

                                <div className="space-y-1.5">
                                    <label htmlFor="thc" className="text-[10px] font-black text-dark-muted uppercase tracking-widest">Avg THC %</label>
                                    <input
                                        id="thc"
                                        type="number"
                                        step="0.1"
                                        value={defaultThc || ''}
                                        onChange={(e) => setDefaultThc(e.target.value ? Number(e.target.value) : undefined)}
                                        className="input h-12 text-sm font-bold bg-dark-bg"
                                        placeholder="0.0"
                                    />
                                </div>
                            </div>
                        </div>

                        <div className="space-y-1.5">
                            <label htmlFor="genetics" className="text-[10px] font-black text-dark-muted uppercase tracking-widest">Heritage (Cross-breeding)</label>
                            <input
                                id="genetics"
                                type="text"
                                value={genetics}
                                onChange={(e) => setGenetics(e.target.value)}
                                className="input h-12 text-sm font-bold bg-dark-bg"
                                placeholder="e.g., Blueberry x Haze"
                            />
                        </div>

                        <div className="space-y-1.5">
                            <label htmlFor="notes" className="text-[10px] font-black text-dark-muted uppercase tracking-widest">Institutional Knowledge</label>
                            <textarea
                                id="notes"
                                value={notes}
                                onChange={(e) => setNotes(e.target.value)}
                                className="input min-h-[100px] resize-none text-sm font-medium bg-dark-bg p-4"
                                placeholder="Common effects, typical terpene profile, or general notes..."
                            />
                        </div>

                        <button
                            type="submit"
                            disabled={saving}
                            className="btn-primary w-full py-4 text-sm font-black uppercase tracking-widest shadow-xl shadow-accent-purple/20 active:scale-95 transition-all"
                        >
                            {saving ? 'Processing...' : (editingId ? 'Commit Changes' : 'Initialize Entry')}
                        </button>
                    </form>
                )}

                {/* Strain List */}
                {strains.length === 0 ? (
                    <EmptyState
                        icon={Sprout}
                        title="Genetic Library Empty"
                        description="Start by adding personal favorites or use the automated import to populate common strains."
                        actionLabel="Add First Strain"
                        actionPath="#"
                    />
                ) : (
                    <div className="grid grid-cols-1 gap-4">
                        {strains
                            .filter(s => {
                                const matchesSearch = s.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                                    s.genetics?.toLowerCase().includes(searchTerm.toLowerCase());
                                const matchesFilter = activeFilter === 'all' || s.strain_type === activeFilter;
                                return matchesSearch && matchesFilter;
                            })
                            .slice(0, 50)
                            .map((strain) => (
                                <div
                                    key={strain.id}
                                    className="card group cursor-pointer hover:border-accent-purple/30 transition-all relative overflow-hidden"
                                    onClick={() => setSelectedStrain(strain)}
                                >
                                    <div className="absolute top-0 right-0 p-4 opacity-[0.03] pointer-events-none group-hover:opacity-10 transition-opacity">
                                        <Sprout size={120} />
                                    </div>

                                    <div className="flex items-start justify-between mb-4 relative z-10">
                                        <div className="flex items-center gap-4">
                                            <div className="p-3 rounded-2xl bg-dark-bg border border-dark-border group-hover:border-accent-purple/30 transition-all">
                                                <Sprout size={20} className="text-accent-purple/60" />
                                            </div>
                                            <div>
                                                <h3 className="font-black text-lg tracking-tight group-hover:text-white transition-colors">{strain.name}</h3>
                                                <div className="flex items-center gap-2 mt-0.5">
                                                    <span className={`text-[8px] font-black uppercase tracking-widest px-1.5 py-0.5 rounded border ${typeColors[strain.strain_type]}`}>
                                                        {strain.strain_type}
                                                    </span>
                                                    {strain.default_thc && (
                                                        <span className="text-[9px] text-dark-muted font-bold flex items-center gap-1 uppercase tracking-tighter">
                                                            <Zap size={10} className="text-accent-green/40" /> {strain.default_thc}% Typical
                                                        </span>
                                                    )}
                                                </div>
                                            </div>
                                        </div>
                                    </div>

                                    {strain.genetics && (
                                        <div className="mb-4 relative z-10">
                                            <p className="text-[10px] font-black text-dark-muted uppercase tracking-widest mb-1">Heritage</p>
                                            <p className="text-xs text-dark-muted font-medium line-clamp-1">{strain.genetics}</p>
                                        </div>
                                    )}

                                    <div className="flex gap-2 pt-4 border-t border-dark-border/50 relative z-10" onClick={(e) => e.stopPropagation()}>
                                        <button
                                            onClick={() => handleEdit(strain)}
                                            className="h-10 px-4 flex-1 text-[10px] font-black uppercase tracking-widest bg-dark-bg border border-dark-border rounded-xl hover:bg-accent-purple/10 hover:border-accent-purple/30 text-dark-muted hover:text-accent-purple transition-all flex items-center justify-center gap-2"
                                        >
                                            <Edit2 size={12} /> Edit
                                        </button>
                                        <button
                                            onClick={() => handleDelete(strain.id)}
                                            className="h-10 px-4 text-[10px] font-black uppercase tracking-widest bg-dark-bg border border-dark-border rounded-xl hover:bg-accent-red/10 hover:border-accent-red/30 text-dark-muted hover:text-accent-red transition-all flex items-center justify-center w-12"
                                        >
                                            <Trash2 size={12} />
                                        </button>
                                    </div>
                                </div>
                            ))}

                        {strains.length > 50 && (
                            <div className="py-8 text-center border-t border-dark-border/30 mt-4">
                                <p className="text-[10px] font-black uppercase tracking-widest text-dark-muted/40">
                                    Displaying {searchTerm ? 'matched results' : 'top 50 genomics'}
                                </p>
                            </div>
                        )}
                    </div>
                )}

                <div className="card bg-dark-surface/30 border-dark-border/40 border-dashed">
                    <div className="flex gap-4">
                        <div className="shrink-0 p-2 bg-dark-bg rounded-lg border border-dark-border self-start">
                            <Info size={16} className="text-accent-purple/60" />
                        </div>
                        <div>
                            <p className="text-xs font-black text-dark-muted uppercase tracking-widest mb-1">Genomic Note</p>
                            <p className="text-[11px] text-dark-muted/60 leading-relaxed font-medium">
                                The Genetic Library stores constant strain data. Use the Product Ledger to link these genetics with specific brands and purchased versions.
                            </p>
                        </div>
                    </div>
                </div>
            </div>

            <StrainDetailsModal
                strain={selectedStrain}
                onClose={() => setSelectedStrain(null)}
            />
        </AppLayout>
    );
}
